/*
 * CAUTION: The values below are relied upon by the P0 CLI. Any changes made here may need to be reflected in and should
 * be tested in the P0 CLI.
 */
import type {
  AccessRequestOf,
  ArgsOf,
  RequestOf,
  SpecToType,
  StagedAccessRequestOf,
} from "@p0security/shared/types/access/index";
import { Access } from "@p0security/shared/types/access/index";
import type { StagedPermissionRequest } from "@p0security/shared/types/permission";

import { awsDelegates } from "./delegates.ts";

const awsCommon = {
  account: {
    type: "static",
    label: "Account",
    description: "The AWS account in which access should be granted",
    optional: true,
    isPreselect: true,
    waitForInput: true,
  },
  accountId: {
    type: "generated",
    label: "Account ID",
    description: "AWS's internal numeric ID for this account",
  },
  idcArn: {
    type: "generated",
    label: "IDC ARN",
    optional: true,
  },
  idcId: { type: "generated", label: "IDC ID", optional: true },
  idcRegion: {
    type: "generated",
    label: "IDC region",
    optional: true,
  },
  userName: {
    type: "generated",
    label: "User name",
    description: "Internal AWS username for this user",
  },
} as const;

const awsNamed = (title: string) =>
  ({
    arn: {
      type: "generated",
      label: `${title} ARN`,
      description: `${title} ARN`,
    },
    name: {
      type: "generated",
      label: `${title} ARN`,
      description: `${title} ARN`,
    },
  }) as const;

export const AwsAccesses = {
  group: Access({
    description: "Add user to AWS IAM group",
    label: "Group",
    maturity: "ga",
    delegation: {},
    generated: {},
    parameters: {
      ...awsCommon,
      name: {
        type: "static",
        label: "Group",
        description: "Group name",
      },
    },
    render: {
      locator: "accountId",
      policy: "name",
    } as const,
  }),
  "permission-set": Access({
    description: "Assign Identity Center permission set",
    label: "Permission set",
    maturity: "ga",
    delegation: {},
    generated: {},
    parameters: {
      ...awsCommon,
      arn: awsNamed("Permission-set").arn,
      // Name is normalized after event creation to always be the permission-set name,
      // although it can be an ARN at request time
      name: {
        type: "static",
        label: "Permission-set",
        description: "Permission-set name or ARN",
      },
    },
    render: {
      locator: "accountId",
      policy: "name",
    } as const,
  }),
  policy: Access({
    description: "Attach AWS policy to user",
    label: "Policy",
    maturity: "ga",
    delegation: awsDelegates.policy,
    generated: {
      /** The ARN of the generated resource the user will adopt (permission set or role) */
      arn: { type: "string" },
      /** The name of the generated resource the user will adopt (permission set or role) */
      name: { type: "string" },
    },
    parameters: {
      ...awsCommon,
      arns: {
        type: "dynamic",
        multivalued: true,
        label: "Policy",
        description: "Managed or custom policy ARN",
      },
      policies: {
        type: "object",
        label: "Policies",
        multivalued: true,
        properties: {
          arn: {
            type: "generated",
            label: "Policy ARN",
          },
          name: {
            type: "generated",
            label: "Policy name",
          },
        },
      },
      extends: {
        type: "static",
        label: "Extend policies",
        description: "Also include all policies from this permission set",
        optional: true,
      },
      extendsName: {
        type: "generated",
        label: "Extend policies",
        description: "Also include all policies from this permission set name",
        optional: true,
      },
    },
    render: {
      locator: "accountId",
      policy: "arns",
    } as const,
  }),
  resource: Access({
    description: "Policies for an AWS resource",
    label: "Resource",
    maturity: "ga",
    delegation: awsDelegates.resource,
    generated: {
      arn: { type: "string", label: "ARN of permission set or role" },
      name: { type: "string", label: "Name of permission set or role" },
      policyArn: { type: "string", label: "Generated policy ARN" },
      policyDocument: { type: "string", label: "Policy JSON" },
      policyName: { type: "string", label: "Generated policy name" },
    },
    parameters: {
      ...awsCommon,
      name: awsNamed("Resource").name,
      arn: {
        type: "dynamic",
        label: "Resource ARN",
        description: "Resource ARN, e.g. arn:aws:s3:::{bucket_name}",
        waitForInput: true,
      },
      object: {
        type: "input",
        optional: true,
        label: "File or folder",
        description: "File or folder within this resource",
      },
      policies: {
        type: "dynamic",
        multivalued: true,
        label: "Policies",
        description: "Managed or custom policy names or ARNs",
      },
      extends: {
        type: "static",
        label: "Extend policies",
        description: "Also include all policies from this permission set",
        optional: true,
      },
      extendsName: {
        type: "generated",
        label: "Extend policies",
        description: "Also include all policies from this permission set name",
        optional: true,
      },
      service: {
        type: "generated",
        label: "Resource service",
        description: "Service that contains the AWS resource",
      },
    },
    render: {
      locator: "accountId",
      policy: "policies",
      resource: "name",
    } as const,
  }),
  role: Access({
    description: "Allow AWS user to assume an IAM role",
    label: "Role",
    maturity: "ga",
    delegation: {},
    generated: {},
    parameters: {
      ...awsCommon,
      name: {
        type: "static",
        label: "Role name",
      },
    },
    render: { locator: "accountId", policy: "name" } as const,
  }),
};

export type AwsAccesses = typeof AwsAccesses;
export type AwsAccess = AwsAccesses[keyof AwsAccesses];

export type AwsGroupAccess = AwsAccesses["group"];
export type AwsPermissionSetAccess = AwsAccesses["permission-set"];
export type AwsPolicyAccess = AwsAccesses["policy"];
export type AwsResourceAccess = AwsAccesses["resource"];
export type AwsRoleAccess = AwsAccesses["role"];

export type AwsGroupRequest = RequestOf<AwsGroupAccess>;
export type AwsPermissionSetRequest = RequestOf<AwsPermissionSetAccess>;
export type AwsPolicyRequest = RequestOf<AwsPolicyAccess>;
export type AwsResourceRequest = RequestOf<AwsResourceAccess>;
export type AwsRoleRequest = RequestOf<AwsRoleAccess>;
export type AwsRequest = RequestOf<AwsAccess>;

export type AwsAccessRequest = AccessRequestOf<AwsAccesses>;
export type StagedAwsAccessRequest = StagedAccessRequestOf<AwsAccesses>;

export type AwsPolicyGenerated = SpecToType<AwsPolicyAccess["generated"]>;
export type AwsResourceGenerated = SpecToType<AwsResourceAccess["generated"]>;

export type AwsArgs = ArgsOf<AwsAccess>;

export const isStagedAwsRequest = (
  request: StagedPermissionRequest
): request is StagedAccessRequestOf<AwsAccesses> & { type: "aws" } =>
  request.type === "aws";
